import classNames from "classnames";
import React from "react";

import { ITestable, testAttr } from "@/browser";

export type TAnchorVariant = "primary" | "secondary" | "info" | "success" | "danger" | "warning" | "link";
export type TAnchorDisplay = "inline" | "inline-block" | "block";
export type TAnchorSize = "xs" | "sm" | "md" | "lg" | "xl";
export type TAnchorShape = "default" | "rounded" | "circle" | "square";

/**
 * Base <a /> element properties
 */
type TAnchorHTMLProps = Omit<JSX.IntrinsicElements["a"], "color">;

/**
 * The Anchor component properties interface
 */
export interface IAnchorProps<
  TVariant = TAnchorVariant,
  TDisplay = TAnchorDisplay,
  TSize = TAnchorSize,
  TShape = TAnchorShape,
> extends ITestable,
    TAnchorHTMLProps {
  /**
   * The variant of the anchor
   * @default link
   */
  variant?: TVariant;

  /**
   * The way of displaying the anchor
   * @default inline
   */
  display?: TDisplay;

  /**
   * The size of the anchor
   * @default md
   */
  size?: TSize;

  /**
   * Determines the shape of the border
   * @default default
   */
  shape?: TShape;

  /**
   * While is set up to true the `anchor--active` class is added which indicates that this element is active
   * @default false
   */
  active?: boolean;
}

/**
 * The Anchor component
 */
export function Anchor<
  TVariant extends string = TAnchorVariant,
  TDisplay extends string = TAnchorDisplay,
  TSize extends string = TAnchorSize,
  TShape extends string = TAnchorShape,
>(
  props: IAnchorProps<TAnchorVariant | TVariant, TAnchorDisplay | TDisplay, TAnchorSize | TSize, TAnchorShape | TShape>,
) {
  const {
    variant = "link",
    display = "inline",
    size = "md",
    shape = "default",
    active = false,
    className,
    testName,
    testValue,
    ...rest
  } = props;
  const variantClass = `anchor--variant-${variant}`;
  const displayClass = `anchor--display-${display}`;
  const sizeClass = `anchor--size-${size}`;
  const shapeClass = `anchor--shape-${shape}`;
  const classes = classNames(
    "anchor",
    variantClass,
    displayClass,
    sizeClass,
    shapeClass,
    {
      "anchor--active": active,
    },
    className,
  );
  const testableProps = testAttr({ testName, testValue }, { sufix: "anchor" });

  return (
    <a
      className={classes}
      {...testableProps}
      {...rest}
      {...(rest.target === "_blank" && { rel: "noopener noreferrer" })}
    />
  );
}
